import { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet";
import { Link, useParams } from "react-router-dom";
import { Dialog } from "@headlessui/react";
import LighthouseCard from "../components/LighthouseCard";
import Contacto from "../components/global/Contacto";

export default function SobreNosotros() {
  const { t } = useTranslation();
  const { lang } = useParams();
  const [openImage, setOpenImage] = useState(false);
  const [openLighthouse, setOpenLighthouse] = useState(false);

  const imagenArtesanal = t("sobreNosotros.artesanal.imagen");
  const imagenMetodo = t("sobreNosotros.metodo.imagen");
  const fraseInspiradora = t("sobreNosotros.frase");
  const [openContacto, setOpenContacto] = useState(false);
  const formRef = useRef();
  const [status, setStatus] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus(null);
    try {
      // Tu lógica de envío aquí
      setStatus("success");
    } catch (error) {
      setStatus("error");
    }
  };

  return (
    <section className="relative min-h-screen">
      {/* Background con gradiente animado */}
      <div className="fixed inset-0 bg-gradient-to-br from-slate-900 via-purple-900/20 to-slate-900 -z-10">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-purple-400/10 via-transparent to-transparent"></div>
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,_var(--tw-gradient-stops))] from-cyan-400/10 via-transparent to-transparent"></div>
        {/* Partículas flotantes */}
        <div className="absolute inset-0 opacity-20">
          {[...Array(6)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-2 h-2 bg-gradient-to-r from-cyan-400 to-purple-400 rounded-full"
              animate={{
                x: [0, 100, 0],
                y: [0, -100, 0],
                opacity: [0.2, 0.8, 0.2]
              }}
              transition={{
                duration: 6 + i * 2,
                repeat: Infinity,
                ease: "easeInOut"
              }}
              style={{
                left: `${10 + i * 15}%`,
                top: `${20 + i * 10}%`
              }}
            />
          ))}
        </div>
      </div>

      <div className="relative px-6 lg:px-24 py-16 text-white">
        <Helmet>
          <title>{t("sobreNosotros.meta.title")}</title>
          <meta name="description" content={t("sobreNosotros.meta.description")} />
        </Helmet>

        {/* Header con efecto glassmorphism */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-20 backdrop-blur-sm bg-white/5 rounded-3xl p-8 border border-white/10 shadow-xl"
        >
          <h2 className="text-5xl lg:text-7xl font-bold bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-600 bg-clip-text text-transparent tracking-wide mb-6">
            {t("sobreNosotros.titulo")}
          </h2>
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: "100px" }}
            transition={{ delay: 0.5, duration: 0.8 }}
            className="h-1 bg-gradient-to-r from-cyan-400 to-purple-600 mx-auto rounded-full mb-6"
          />
          <p className="max-w-4xl mx-auto text-xl text-gray-300 leading-relaxed">
            {t("sobreNosotros.descripcion")}
          </p>
        </motion.div>

        {/* +95 en Lighthouse garantizado */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="mb-24"
        >
          <LighthouseCard onOpenModal={() => setOpenLighthouse(true)} />
        </motion.div>

        {/* Método Zennith + Cómo trabajamos */}
        <div className="grid lg:grid-cols-2 gap-16 items-center max-w-7xl mx-auto mb-32">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="backdrop-blur-md bg-gradient-to-br from-white/10 to-white/5 rounded-2xl p-8 border border-white/10 shadow-2xl hover:shadow-cyan-500/10 transition-all duration-500"
          >
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-gradient-to-r from-cyan-400 to-blue-500 rounded-xl flex items-center justify-center mr-4">
                <span className="text-2xl">⚡</span>
              </div>
              <h3 className="text-3xl lg:text-4xl font-bold bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
                {t("sobreNosotros.metodo.titulo")}
              </h3>
            </div>
            <ol className="space-y-4">
              {t("sobreNosotros.metodo.pasos", { returnObjects: true }).map((item, i) => (
                <motion.li
                  key={i}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.1, duration: 0.5 }}
                  viewport={{ once: true }}
                  className="flex items-start space-x-3"
                >
                  <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 mt-1">
                    {i + 1}
                  </div>
                  <p className="text-gray-300 text-lg leading-relaxed">{item}</p>
                </motion.li>
              ))}
            </ol>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="hidden lg:block"
          >
            {imagenMetodo && (
              <div className="relative group">
                <div className="absolute -inset-1 bg-gradient-to-r from-cyan-400 to-purple-600 rounded-2xl blur opacity-25 group-hover:opacity-75 transition duration-1000"></div>
                <img
                  src={imagenMetodo}
                  alt="Método Zennith"
                  className="relative rounded-2xl shadow-2xl border border-white/20 w-full h-auto transform group-hover:scale-105 transition-all duration-500"
                  onError={() => console.warn("No se pudo cargar imagen:", imagenMetodo)}
                />
              </div>
            )}
          </motion.div>
        </div>

        {/* ¿Por qué código artesanal? + Imagen */}
        <div className="grid lg:grid-cols-2 gap-16 items-center max-w-7xl mx-auto mb-32">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="lg:order-2 backdrop-blur-md bg-gradient-to-br from-white/10 to-white/5 rounded-2xl p-8 border border-white/10 shadow-2xl"
          >
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl flex items-center justify-center mr-4">
                <span className="text-2xl">🎨</span>
              </div>
              <h3 className="text-3xl lg:text-4xl font-bold bg-gradient-to-r from-purple-500 to-pink-500 bg-clip-text text-transparent">
                {t("sobreNosotros.artesanal.titulo")}
              </h3>
            </div>
            <p className="text-gray-300 text-lg leading-relaxed mb-6">
              {t("sobreNosotros.artesanal.descripcion")}
            </p>
            <ul className="space-y-3">
              {t("sobreNosotros.artesanal.lista", { returnObjects: true }).map((item, i) => (
                <motion.li
                  key={i}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.1, duration: 0.5 }}
                  viewport={{ once: true }}
                  className="flex items-start space-x-3"
                >
                  <div className="w-2 h-2 bg-gradient-to-r from-cyan-400 to-purple-600 rounded-full mt-3 flex-shrink-0"></div>
                  <p className="text-gray-300 text-lg">{item}</p>
                </motion.li>
              ))}
            </ul>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="lg:order-1 text-center"
          >
            <div className="relative group">
              <div className="absolute -inset-1 bg-gradient-to-r from-purple-500 to-pink-500 rounded-2xl blur opacity-25 group-hover:opacity-75 transition duration-1000"></div>
              <img
                src={imagenArtesanal}
                alt="Código artesanal Zennith"
                className="relative rounded-2xl shadow-2xl border border-white/20 cursor-pointer transition-all duration-500 hover:scale-105"
                onClick={() => setOpenImage(true)}
              />
            </div>
            <p className="text-sm text-gray-400 mt-4 italic font-medium">
              {t("sobreNosotros.artesanal.subtexto")}
            </p>
          </motion.div>
        </div>

        {/* Modal imagen artesanal */}
        <Dialog open={openImage} onClose={() => setOpenImage(false)} className="z-50 relative">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 z-50"
          >
            <Dialog.Panel className="relative max-w-6xl w-full">
              <button
                onClick={() => setOpenImage(false)}
                className="absolute top-4 right-4 w-12 h-12 bg-white/10 backdrop-blur-md rounded-full flex items-center justify-center text-white text-2xl hover:bg-white/20 transition border border-white/20"
                aria-label="Cerrar"
              >
                ×
              </button>
              <motion.img
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.3 }}
                src={imagenArtesanal}
                alt="Código ampliado"
                className="w-full rounded-2xl border border-white/30 shadow-2xl"
              />
            </Dialog.Panel>
          </motion.div>
        </Dialog>

        {/* Grid de secciones informativas */}
        <div className="grid md:grid-cols-3 gap-8 max-w-7xl mx-auto mb-32">
          {/* Lo que evitamos */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="backdrop-blur-md bg-gradient-to-br from-red-500/10 to-red-600/5 rounded-2xl p-8 border border-red-500/20 shadow-xl hover:shadow-red-500/10 transition-all duration-500"
          >
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-gradient-to-r from-red-500 to-red-600 rounded-xl flex items-center justify-center mr-4">
                <span className="text-2xl">🚫</span>
              </div>
              <h3 className="text-2xl font-bold bg-gradient-to-r from-red-400 to-red-600 bg-clip-text text-transparent">
                {t("sobreNosotros.queNo.titulo")}
              </h3>
            </div>
            <ul className="space-y-3">
              {t("sobreNosotros.queNo.lista", { returnObjects: true }).map((item, i) => (
                <li key={i} className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-gray-300 text-sm">{item}</p>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Stack tecnológico */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            viewport={{ once: true }}
            className="backdrop-blur-md bg-gradient-to-br from-green-500/10 to-green-600/5 rounded-2xl p-8 border border-green-500/20 shadow-xl hover:shadow-green-500/10 transition-all duration-500"
          >
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-green-600 rounded-xl flex items-center justify-center mr-4">
                <span className="text-2xl">🔧</span>
              </div>
              <h3 className="text-2xl font-bold bg-gradient-to-r from-green-400 to-green-600 bg-clip-text text-transparent">
                {t("sobreNosotros.stack.titulo")}
              </h3>
            </div>
            <ul className="space-y-3">
              {t("sobreNosotros.stack.lista", { returnObjects: true }).map((item, i) => (
                <li key={i} className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-gray-300 text-sm">{item}</p>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Filosofía */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.6 }}
            viewport={{ once: true }}
            className="backdrop-blur-md bg-gradient-to-br from-blue-500/10 to-blue-600/5 rounded-2xl p-8 border border-blue-500/20 shadow-xl hover:shadow-blue-500/10 transition-all duration-500"
          >
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl flex items-center justify-center mr-4">
                <span className="text-2xl">💭</span>
              </div>
              <h3 className="text-2xl font-bold bg-gradient-to-r from-blue-400 to-blue-600 bg-clip-text text-transparent">
                {t("sobreNosotros.filosofia.titulo")}
              </h3>
            </div>
            <p className="text-gray-300 text-sm leading-relaxed whitespace-pre-line">
              {t("sobreNosotros.filosofia.descripcion")}
            </p>
          </motion.div>
        </div>

        {/* Frase inspiracional */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center max-w-5xl mx-auto mb-32"
        >
          <div className="relative">
            <div className="absolute -inset-4 bg-gradient-to-r from-cyan-400/20 to-purple-600/20 rounded-3xl blur"></div>
            <blockquote className="relative backdrop-blur-md bg-white/5 rounded-3xl p-12 border border-white/10 shadow-2xl">
              <div className="text-6xl text-cyan-400 mb-4">"</div>
              <p className="text-2xl lg:text-3xl font-medium bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-600 bg-clip-text text-transparent leading-relaxed">
                {fraseInspiradora}
              </p>
              <div className="text-6xl text-purple-600 mt-4 text-right">"</div>
            </blockquote>
          </div>
        </motion.div>

        {/* CTA Final */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <div className="backdrop-blur-md bg-gradient-to-br from-white/10 to-white/5 rounded-3xl p-12 border border-white/10 shadow-2xl max-w-4xl mx-auto">
            <h4 className="text-3xl lg:text-4xl font-bold bg-gradient-to-r from-cyan-400 to-purple-600 bg-clip-text text-transparent mb-4">
              {t("sobreNosotros.proyectos.titulo")}
            </h4>
            <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
              {t("sobreNosotros.proyectos.descripcion")}
            </p>
            <motion.button
              onClick={() => setOpenContacto(true)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="relative group inline-block"
            >
              <div className="absolute -inset-1 bg-gradient-to-r from-cyan-400 to-purple-600 rounded-full blur opacity-75 group-hover:opacity-100 transition duration-1000"></div>
              <div className="relative bg-gradient-to-r from-cyan-400 to-purple-600 text-black px-12 py-4 rounded-full font-bold text-xl hover:shadow-2xl transition-all duration-300 cursor-pointer">
                {t("sobreNosotros.proyectos.boton")}
              </div>
            </motion.button>
          </div>
        </motion.div>

        {/* Modal de contacto */}
        <Dialog open={openContacto} onClose={() => setOpenContacto(false)} className="z-50 relative">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50 overflow-auto"
          >
            <Dialog.Panel className="relative max-w-3xl w-full backdrop-blur-md bg-gradient-to-br from-slate-800/90 to-slate-900/90 text-white p-8 rounded-3xl shadow-2xl border border-white/10">
              <button
                onClick={() => setOpenContacto(false)}
                className="absolute top-4 right-4 w-12 h-12 bg-white/10 backdrop-blur-md rounded-full flex items-center justify-center text-white text-2xl hover:bg-white/20 transition border border-white/20"
                aria-label="Cerrar contacto"
              >
                ×
              </button>
              <Contacto
                formRef={formRef}
                handleSubmit={handleSubmit}
                status={status}
              />
            </Dialog.Panel>
          </motion.div>
        </Dialog>
      </div>
    </section>
  );
}